import apiClient from './client';
import { useAuthStore } from '../stores/authStore';
import { API_BASE_URL } from '../utils/constants';

export interface LegalDocumentItem {
  key: string;
  audience: string;
  category: string;
  title: string;
  version: string;
  version_hash: string;
  url: string;
  affirmation_keys: string[];
}

export interface LegalDocumentsResponse {
  language: string;
  documents: LegalDocumentItem[];
}

export interface LegalRequiredItem {
  doc_key: string;
  audience: string;
  category: string;
  version: string;
  version_hash: string;
  accepted: boolean;
  accepted_at: string | null;
  certificate_url: string | null;
}

export interface OutstandingItem {
  doc_key: string;
  sub_user_id: string | null;
  sub_user_name: string | null;
}

export interface LegalStatusResponse {
  allowed: boolean;
  is_managed_sub_user: boolean;
  blocked_by_guardian: boolean;
  required: LegalRequiredItem[];
  outstanding: OutstandingItem[];
}

export interface SubmitAgreementPayload {
  doc_key: string;
  language: string;
  signed_legal_name: string | null;
  minor_legal_name?: string | null;
  sub_user_id?: string | null;
  scrolled_completed: boolean;
  affirmations: Record<string, boolean>;
  doc_title?: string | null;
  affirmation_labels?: Record<string, string> | null;
  app_version?: string | null;
  device_info?: string | null;
  user_agent?: string | null;
}

export interface LegalAgreementRead {
  id: string;
  user_id: string;
  doc_key: string;
  doc_version: string;
  version_hash: string;
  audience: string;
  category: string;
  signed_legal_name: string;
  minor_legal_name: string | null;
  sub_user_id: string | null;
  language: string;
  ip_address: string | null;
  user_agent: string | null;
  app_version: string | null;
  device_info: string | null;
  scrolled_completed: boolean;
  affirmations: Record<string, boolean> | null;
  doc_title: string | null;
  affirmation_labels: Record<string, string> | null;
  certificate_url: string | null;
  created_at: string;
}

export const legalApi = {
  getDocuments: (language = 'en') =>
    apiClient.get<LegalDocumentsResponse>('/legal/documents', { params: { language } }).then((r) => r.data),

  getStatus: () =>
    apiClient.get<LegalStatusResponse>('/legal/status').then((r) => r.data),

  submitAgreement: (payload: SubmitAgreementPayload) =>
    apiClient.post<LegalAgreementRead>('/legal/agreements', payload).then((r) => r.data),

  getAgreements: () =>
    apiClient.get<LegalAgreementRead[]>('/legal/agreements').then((r) => r.data),

  getProxyDocUrl: (docKey: string, language = 'en', versionHash?: string): string => {
    const token = useAuthStore.getState().token || '';
    const params = new URLSearchParams({ doc_key: docKey, language, token });
    if (versionHash) params.set('v', versionHash.slice(0, 8));
    return `${API_BASE_URL}/legal/proxy-doc?${params.toString()}`;
  },

  getPublicProxyDocUrl: (docType: 'privacy_policy' | 'terms_of_service', language = 'en'): string => {
    const params = new URLSearchParams({ doc_type: docType, language });
    return `${API_BASE_URL}/legal/public-doc?${params.toString()}`;
  },
};
